import { Input } from '@atoms/index';
import type { RestaurantApplicationFormData } from '@services/complianceService';
import {
  APPLICATION_FORM_FIELDS,
  type ApplicationFormFieldErrors,
  type ApplicationFormFieldKey,
  validateApplicationFormField,
} from '@utils/applicationFormValidation';
import {
  normalizeDigitsInput,
  normalizeIbanInput,
  normalizePhoneInput,
} from '@utils/validation';

interface RestaurantApplicationFormFieldsProps {
  formData: RestaurantApplicationFormData;
  fieldErrors: ApplicationFormFieldErrors;
  onChange: (next: RestaurantApplicationFormData) => void;
  onFieldBlur: (key: ApplicationFormFieldKey) => void;
}

function formatFieldValue(key: ApplicationFormFieldKey, value: string): string {
  switch (key) {
    case 'taxNumber':
      return normalizeDigitsInput(value, 11);
    case 'iban':
      return normalizeIbanInput(value);
    case 'authorizedPersonPhone':
      return normalizePhoneInput(value);
    case 'phone': {
      const digits = value.replace(/\D/g, '').slice(0, 11);
      if (digits.startsWith('0')) return digits.slice(0, 11);
      return digits.slice(0, 10);
    }
    case 'mersisNumber':
      if (/^\d*$/.test(value.replace(/\s/g, ''))) {
        return normalizeDigitsInput(value, 16);
      }
      return value.slice(0, 30);
    default:
      return value;
  }
}

export function RestaurantApplicationFormFields({
  formData,
  fieldErrors,
  onChange,
  onFieldBlur,
}: RestaurantApplicationFormFieldsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {APPLICATION_FORM_FIELDS.map((field) => (
        <div
          key={field.key}
          className={field.fullWidth || field.key === 'address' ? 'md:col-span-2' : ''}
        >
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label}
            {field.required && <span className="text-primary ml-0.5">*</span>}
          </label>
          <Input
            value={formData[field.key] ?? ''}
            placeholder={field.placeholder}
            helperText={fieldErrors[field.key] ? undefined : field.helperText}
            error={fieldErrors[field.key]}
            inputMode={field.inputMode}
            type={field.key === 'formDate' ? 'date' : field.inputMode === 'email' ? 'email' : 'text'}
            onChange={(e) => {
              const formatted = formatFieldValue(field.key, e.target.value);
              onChange({ ...formData, [field.key]: formatted });
            }}
            onBlur={() => onFieldBlur(field.key)}
          />
        </div>
      ))}
    </div>
  );
}

export function validateSingleApplicationFormField(
  key: ApplicationFormFieldKey,
  value: string,
): string | undefined {
  const result = validateApplicationFormField(key, value);
  if (key === 'authorizedPersonPhone' && result.valid && !value.trim()) {
    return 'Yetkili cep telefonu zorunludur.';
  }
  return result.valid ? undefined : result.message;
}
